import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { formatDate, formatCurrency, getInitials } from '@/lib/utils';
import { Search, Eye, Shield, Ban, CheckCircle, KeyRound, Trash2, Star, Package, ShoppingBag, Gavel, AlertTriangle, X, MessageCircle, Plus, Minus, Activity, MessagesSquare, FileText, Save, Loader2, User, Edit, History, LogIn, Monitor, Smartphone, Globe } from 'lucide-react';
import { adminAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';

const getRoleBadge = (role) => {
    const roles = {
        admin: { label: 'Admin', className: 'bg-red-100 text-red-800' },
        moderator: { label: 'Moderator', className: 'bg-blue-100 text-blue-800' },
        user: { label: 'Utilizator', className: 'bg-gray-100 text-gray-800' }
    };
    const r = roles[role] || roles.user;
    return <Badge className={r.className}>{r.label}</Badge>;
};

const AdminUsersTab = ({
    currentUserRole,
    onInvestigate,
    onSetRoleDialog,
    onSetBanDialog,
    onUnbanUser,
    onRefreshUsers
}) => {
    const navigate = useNavigate();
    
    // Users data state with infinite scroll
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const perPage = 30;
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    
    // User detail panel state
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetailLoading, setUserDetailLoading] = useState(false);
    const [userDetailData, setUserDetailData] = useState(null);
    
    // Password dialog state
    const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null });
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Role dialog state
    const [roleDialog, setRoleDialog] = useState({ open: false, user: null, newRole: 'user' });
    
    // Ban dialog state
    const [banDialog, setBanDialog] = useState({ open: false, user: null, reason: '' });
    
    // Delete dialog state
    const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
    
    // Message dialog state
    const [messageDialog, setMessageDialog] = useState({ open: false, user: null });
    const [messageContent, setMessageContent] = useState('');
    
    // Admin notes state
    const [adminNotes, setAdminNotes] = useState('');
    const [adminNotesLoading, setAdminNotesLoading] = useState(false);
    const [adminNotesSaving, setAdminNotesSaving] = useState(false);
    
    // User notes (from reports) state
    const [userNotesDialog, setUserNotesDialog] = useState({ open: false, user: null, notes: [], loading: false });
    const [addNoteDialog, setAddNoteDialog] = useState({ open: false, user: null, note: '' });
    const [addingNote, setAddingNote] = useState(false);
    
    // Edit name dialog state
    const [editNameDialog, setEditNameDialog] = useState({ open: false, user: null, newUsername: '', newName: '' });

    // Login log dialog state
    const [loginLogDialog, setLoginLogDialog] = useState({ open: false, user: null, logs: [], loading: false });

    // Fetch users function
    const fetchUsers = useCallback(async (pageNum = 1, reset = false) => {
        try {
            if (pageNum === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            const params = {
                skip: (pageNum - 1) * perPage,
                limit: perPage,
            };

            if (searchQuery.trim()) {
                params.search = searchQuery.trim();
            }

            const response = await api.get('/admin/users', { params });
            const newUsers = response.data?.users || response.data || [];
            const total = response.data?.total || newUsers.length;

            if (reset || pageNum === 1) {
                setUsers(newUsers);
                setPage(1);
            } else {
                setUsers(prev => [...prev, ...newUsers]);
            }

            setTotalCount(total);
            setHasMore(newUsers.length === perPage);

        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Eroare la încărcarea utilizatorilor');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [searchQuery]);

    // Load more users
    const loadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchUsers(nextPage, false);
        }
    }, [page, loadingMore, hasMore, fetchUsers]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        observerRef.current = observer;

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [loadMore, hasMore, loadingMore, loading]);

    // Initial load
    useEffect(() => {
        fetchUsers(1, true);
    }, []);

    // Debounced search effect
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            fetchUsers(1, true);
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Filter users (now just use fetched users directly since API handles search)
    const filteredUsers = users;

    // Handle browser back button on mobile when user panel is open
    useEffect(() => {
        if (selectedUser) {
            window.history.pushState({ userPanelOpen: true }, '');
            
            const handlePopState = () => {
                setSelectedUser(null);
                setUserDetailData(null);
            };
            
            window.addEventListener('popstate', handlePopState);
            
            return () => {
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [selectedUser]);

    // Fetch user details when clicking on name or email
    const handleUserClick = async (user) => {
        setSelectedUser(user);
        setUserDetailLoading(true);
        setAdminNotes('');
        try {
            const [detailRes, notesRes] = await Promise.all([
                api.get(`/admin/fraud/investigate/${user.user_id}`),
                adminAPI.getUserNotes(user.user_id).catch(() => ({ data: { admin_notes: '' } }))
            ]);
            setUserDetailData(detailRes.data);
            setAdminNotes(notesRes.data?.admin_notes || '');
        } catch (error) {
            console.error('Error fetching user details:', error);
            toast.error('Eroare la încărcarea detaliilor utilizatorului');
        } finally {
            setUserDetailLoading(false);
        }
    };

    const handleCloseUserPanel = () => {
        // Go back in history if we pushed a state
        if (window.history.state?.userPanelOpen) {
            window.history.back();
        } else {
            setSelectedUser(null);
            setUserDetailData(null);
            setAdminNotes('');
        }
    };

    // Save admin notes
    const handleSaveAdminNotes = async () => {
        if (!selectedUser) return;
        setAdminNotesSaving(true);
        try {
            await adminAPI.updateUserNotes(selectedUser.user_id, adminNotes);
            toast.success('Observațiile au fost salvate');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la salvarea observațiilor');
        } finally {
            setAdminNotesSaving(false);
        }
    };

    // Warning handlers
    const handleAddWarning = async (user) => {
        try {
            const res = await adminAPI.addWarning(user.user_id);
            toast.success(res.data.message);
            // Update selected user's warning count locally without closing panel
            // Use functional update to avoid stale closures
            setSelectedUser(prev => {
                if (!prev || prev.user_id !== user.user_id) return prev;
                return {
                    ...prev,
                    warnings_count: res.data.warnings_count,
                    is_banned: res.data.blocked || prev.is_banned,
                    status: res.data.blocked ? 'blocked' : prev.status
                };
            });
            // Don't refresh users list immediately - it would overwrite our local state
            // The list will be refreshed when user closes the panel or navigates away
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la adăugarea avertismentului');
        }
    };

    const handleRemoveWarning = async (user) => {
        try {
            const res = await adminAPI.removeWarning(user.user_id);
            toast.success(res.data.message);
            // Update selected user's warning count locally without closing panel
            setSelectedUser(prev => {
                if (!prev || prev.user_id !== user.user_id) return prev;
                return {
                    ...prev,
                    warnings_count: res.data.warnings_count
                };
            });
            // Don't refresh users list immediately
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la eliminarea avertismentului');
        }
    };

    // View user activity log
    const handleViewUserLog = (user) => {
        // Navigate to logs tab with user filter
        window.location.href = `/admin?tab=logs&user=${user.user_id}`;
    };

    // Fetch user notes from reports
    const handleViewUserNotes = async (user) => {
        setUserNotesDialog({ open: true, user, notes: [], loading: true });
        try {
            const res = await api.get('/reports');
            const allReports = res.data || [];
            // Filter notes for this user by user_id OR username
            const userNotes = allReports
                .filter(r => {
                    const hasNote = r.admin_notes && r.admin_notes.trim() !== '';
                    const matchesUser = r.target_user_id === user.user_id || r.target_username === user.username;
                    return hasNote && matchesUser;
                })
                .map(r => ({
                    report_id: r.report_id,
                    note: r.admin_notes,
                    author: r.admin_notes_by_name || r.admin_notes_by_username || 'Necunoscut',
                    date: r.admin_notes_at || r.created_at,
                    reason: r.reason,
                    status: r.status
                }))
                .sort((a, b) => new Date(b.date) - new Date(a.date));
            setUserNotesDialog(prev => ({ ...prev, notes: userNotes, loading: false }));
        } catch (error) {
            console.error('Error fetching user notes:', error);
            toast.error('Eroare la încărcarea notițelor');
            setUserNotesDialog(prev => ({ ...prev, loading: false }));
        }
    };

    // Open add note dialog
    const handleOpenAddNote = (user) => {
        setAddNoteDialog({ open: true, user, note: '' });
    };

    // Add a new note for user
    const handleAddUserNote = async () => {
        if (!addNoteDialog.user || !addNoteDialog.note.trim()) return;
        
        setAddingNote(true);
        try {
            await adminAPI.addUserAdminNote(addNoteDialog.user.user_id, addNoteDialog.note);
            toast.success('Notița a fost adăugată');
            setAddNoteDialog({ open: false, user: null, note: '' });
            // Refresh notes if dialog is open
            if (userNotesDialog.open && userNotesDialog.user?.user_id === addNoteDialog.user.user_id) {
                handleViewUserNotes(addNoteDialog.user);
            }
        } catch (error) {
            console.error('Error adding note:', error);
            toast.error('Eroare la adăugarea notiței');
        } finally {
            setAddingNote(false);
        }
    };

    // Password handlers
    const handleOpenPasswordDialog = (user) => {
        setPasswordDialog({ open: true, user });
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleClosePasswordDialog = () => {
        setPasswordDialog({ open: false, user: null });
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleChangePassword = async () => {
        if (!newPassword || newPassword.length < 8) {
            toast.error('Parola trebuie să aibă minim 8 caractere');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Parolele nu coincid');
            return;
        }

        if (!passwordDialog.user?.user_id) {
            toast.error('Utilizatorul nu a fost selectat corect');
            return;
        }

        setIsSubmitting(true);
        try {
            console.log('Changing password for user:', passwordDialog.user.user_id);
            console.log('New password length:', newPassword.length);
            const response = await adminAPI.setUserPassword(passwordDialog.user.user_id, newPassword);
            console.log('Password change response:', response.data);
            toast.success(response.data?.message || 'Parola a fost schimbată cu succes');
            handleClosePasswordDialog();
            // Refresh users list after password change
            if (onRefreshUsers) onRefreshUsers();
        } catch (error) {
            console.error('Password change error:', error);
            console.error('Error response:', error.response?.data);
            const errorMessage = error.response?.data?.detail || error.message || 'Eroare la schimbarea parolei';
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Role handlers
    const handleChangeRole = async () => {
        if (!roleDialog.user) return;
        setIsSubmitting(true);
        try {
            await api.put(`/admin/users/${roleDialog.user.user_id}`, { role: roleDialog.newRole });
            toast.success('Rol actualizat cu succes');
            setRoleDialog({ open: false, user: null, newRole: 'user' });
            if (onRefreshUsers) onRefreshUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la schimbarea rolului');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Ban handlers
    const handleBanUser = async () => {
        if (!banDialog.user) return;
        setIsSubmitting(true);
        try {
            await api.post(`/admin/users/${banDialog.user.user_id}/ban`, { reason: banDialog.reason });
            toast.success('Utilizator blocat');
            setBanDialog({ open: false, user: null, reason: '' });
            if (onRefreshUsers) onRefreshUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la blocarea utilizatorului');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete handlers
    const handleDeleteUser = async () => {
        if (!deleteDialog.user) return;
        setIsSubmitting(true);
        try {
            await api.delete(`/admin/users/${deleteDialog.user.user_id}`);
            toast.success('Utilizator șters');
            setDeleteDialog({ open: false, user: null });
            handleCloseUserPanel();
            if (onRefreshUsers) onRefreshUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la ștergerea utilizatorului');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Login log handlers
    const handleOpenLoginLog = async (user) => {
        setLoginLogDialog({ open: true, user, logs: [], loading: true });
        try {
            const res = await api.get(`/admin/users/${user.user_id}/login-history`);
            setLoginLogDialog(prev => ({ ...prev, logs: res.data.logins || [], loading: false }));
        } catch (error) {
            toast.error('Eroare la încărcarea istoricului de login');
            setLoginLogDialog(prev => ({ ...prev, loading: false }));
        }
    };

    const parseUserAgentShort = (ua) => {
        if (!ua || ua === 'unknown') return { device: 'Unknown', browser: 'Unknown' };
        let device = 'Desktop';
        let browser = 'Unknown';
        if (/mobile/i.test(ua)) device = 'Mobile';
        else if (/tablet|ipad/i.test(ua)) device = 'Tablet';
        if (/chrome/i.test(ua) && !/edge|edg/i.test(ua)) browser = 'Chrome';
        else if (/firefox/i.test(ua)) browser = 'Firefox';
        else if (/safari/i.test(ua) && !/chrome/i.test(ua)) browser = 'Safari';
        else if (/edge|edg/i.test(ua)) browser = 'Edge';
        return { device, browser };
    };

    // Message handlers
    const handleSendMessage = async () => {
        if (!messageDialog.user || !messageContent.trim()) {
            toast.error('Mesajul nu poate fi gol');
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await api.post('/messages/new', {
                recipient_id: messageDialog.user.user_id,
                content: messageContent.trim()
            });
            toast.success('Mesaj trimis cu succes!');
            setMessageDialog({ open: false, user: null });
            setMessageContent('');
            // Navigate to messages page
            navigate(`/messages/${response.data.conversation_id}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la trimiterea mesajului');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Edit name handlers
    const handleOpenEditNameDialog = (user) => {
        setEditNameDialog({
            open: true,
            user,
            newUsername: user.username || '',
            newName: user.name || ''
        });
    };

    const handleSaveUserName = async () => {
        if (!editNameDialog.user) return;
        
        const { newUsername, newName } = editNameDialog;
        
        if (!newUsername.trim()) {
            toast.error('Username-ul nu poate fi gol');
            return;
        }
        
        if (newUsername.length < 3) {
            toast.error('Username-ul trebuie să aibă minim 3 caractere');
            return;
        }
        
        setIsSubmitting(true);
        try {
            await api.put(`/admin/users/${editNameDialog.user.user_id}`, {
                username: newUsername.trim(),
                name: newName.trim() || null
            });
            toast.success('Numele a fost actualizat cu succes');
            setEditNameDialog({ open: false, user: null, newUsername: '', newName: '' });
            
            // Update selected user locally
            setSelectedUser(prev => {
                if (!prev || prev.user_id !== editNameDialog.user.user_id) return prev;
                return {
                    ...prev,
                    username: newUsername.trim(),
                    name: newName.trim() || null
                };
            });
            
            if (onRefreshUsers) onRefreshUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la actualizarea numelui');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Caută utilizatori..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="admin-users-search"
                    />
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-4">
                {/* Users List/Table */}
                <div className={selectedUser ? 'lg:flex-1 lg:min-w-[400px] lg:overflow-hidden' : 'w-full'}>
                    <Card>
                        <CardContent className="p-0">
                            {/* Mobile: Card Layout */}
                            <div className="md:hidden divide-y">
                                {filteredUsers.map((u) => (
                                    <div 
                                        key={u.user_id}
                                        className={`p-4 cursor-pointer hover:bg-muted/30 ${selectedUser?.user_id === u.user_id ? 'bg-primary/5' : ''}`}
                                        onClick={() => handleUserClick(u)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Avatar className="w-10 h-10">
                                                <AvatarImage src={u.picture} alt={u.name || u.username} />
                                                <AvatarFallback>{getInitials(u.name || u.username)}</AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-medium text-primary">{u.name || u.username}</span>
                                                {getRoleBadge(u.role)}
                                            </div>
                                            <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                {u.is_banned ? (
                                                    <Badge variant="destructive" className="text-xs">Blocat</Badge>
                                                ) : u.fraud_flagged ? (
                                                    <Badge className="bg-orange-100 text-orange-800 text-xs">Suspect</Badge>
                                                ) : u.is_verified ? (
                                                    <Badge className="bg-green-100 text-green-800 text-xs">Verificat</Badge>
                                                ) : (
                                                    <Badge variant="secondary" className="text-xs">Activ</Badge>
                                                )}
                                                <span className="text-xs text-muted-foreground">{u.rating?.toFixed(1) || '0.0'} ⭐</span>
                                            </div>
                                        </div>
                                        <Eye className="w-4 h-4 text-muted-foreground" />
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {/* Desktop: Table Layout */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-muted/50">
                                    <tr>
                                        <th className="text-left p-4">Utilizator</th>
                                        <th className="text-left p-4">Email</th>
                                        <th className="text-left p-4">Rol</th>
                                        <th className="text-left p-4">Status</th>
                                        <th className="text-left p-4 hidden lg:table-cell">Rating</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map((u) => (
                                        <tr 
                                            key={u.user_id} 
                                            className={`border-t hover:bg-muted/30 cursor-pointer ${selectedUser?.user_id === u.user_id ? 'bg-primary/5' : ''}`}
                                            onClick={() => handleUserClick(u)}
                                        >
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="w-8 h-8">
                                                        <AvatarImage src={u.picture} alt={u.name || u.username} />
                                                        <AvatarFallback>{getInitials(u.name || u.username)}</AvatarFallback>
                                                    </Avatar>
                                                    <span className="font-medium text-primary hover:underline">{u.name || u.username}</span>
                                                </div>
                                            </td>
                                            <td className="p-4 text-sm text-muted-foreground">
                                                <span className="hover:text-primary hover:underline">{u.email}</span>
                                            </td>
                                            <td className="p-4">{getRoleBadge(u.role)}</td>
                                            <td className="p-4">
                                                {u.is_banned ? (
                                                    <Badge variant="destructive">Blocat</Badge>
                                                ) : u.fraud_flagged ? (
                                                    <Badge className="bg-orange-100 text-orange-800">Suspect</Badge>
                                                ) : u.is_verified ? (
                                                    <Badge className="bg-green-100 text-green-800">Verificat</Badge>
                                                ) : (
                                                    <Badge variant="secondary">Activ</Badge>
                                                )}
                                            </td>
                                            <td className="p-4 hidden lg:table-cell">
                                                <span className="text-sm">{u.rating?.toFixed(1) || '0.0'} ⭐</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>

                {/* Infinite Scroll Loader */}
                {hasMore && (
                    <div ref={loadMoreRef} className="flex justify-center py-4">
                        {loadingMore && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Se încarcă mai mulți utilizatori...</span>
                            </div>
                        )}
                    </div>
                )}
                
                {!hasMore && users.length > 0 && (
                    <p className="text-center text-sm text-muted-foreground py-4">
                        Ai ajuns la final ({totalCount} utilizatori)
                    </p>
                )}
                </div>

                {/* User Detail Panel - Full screen modal on mobile */}
                {selectedUser && (
                    <div className="fixed inset-0 z-50 bg-background lg:relative lg:inset-auto lg:z-auto lg:bg-transparent">
                        {/* Mobile header with back button */}
                        <div className="lg:hidden flex items-center gap-3 p-4 border-b bg-background sticky top-0 z-10">
                            <Button variant="ghost" size="icon" onClick={handleCloseUserPanel} data-testid="close-user-detail">
                                <X className="w-5 h-5" />
                            </Button>
                            <span className="font-semibold">Detalii utilizator</span>
                        </div>
                        <Card className="h-[calc(100%-57px)] lg:h-auto lg:w-[420px] lg:min-w-[400px] lg:shrink-0 rounded-none lg:rounded-2xl overflow-auto border-0 lg:border">
                        <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <Avatar className="w-12 h-12">
                                        <AvatarImage src={selectedUser.picture} alt={selectedUser.name || selectedUser.username} />
                                        <AvatarFallback className="text-lg">{getInitials(selectedUser.name || selectedUser.username)}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <CardTitle className="text-lg">{selectedUser.name || selectedUser.username}</CardTitle>
                                        <CardDescription>
                                            {selectedUser.email}
                                        </CardDescription>
                                    </div>
                                </div>
                                <Button variant="ghost" size="icon" onClick={handleCloseUserPanel} className="hidden lg:flex">
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {userDetailLoading ? (
                                <div className="py-8 text-center text-muted-foreground">Se încarcă...</div>
                            ) : userDetailData ? (
                                <div className="space-y-4">
                                    {/* Basic Info */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="p-3 bg-muted/30 rounded-lg">
                                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                                <Star className="w-4 h-4" />
                                                Rating
                                            </div>
                                            <p className="font-semibold text-lg">{userDetailData.user?.rating?.toFixed(1) || '0.0'} ⭐</p>
                                        </div>
                                        <div className="p-3 bg-muted/30 rounded-lg">
                                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                                                <Package className="w-4 h-4" />
                                                Listări totale
                                            </div>
                                            <p className="font-semibold text-lg">{userDetailData.listings?.all?.length || 0}</p>
                                        </div>
                                    </div>

                                    {/* Auction Stats */}
                                    <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                            <Gavel className="w-4 h-4 text-blue-600" />
                                            Statistici Licitații
                                        </h4>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Participări:</span>
                                                <span className="font-medium">{userDetailData.auctions?.participated || 0}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Câștigate:</span>
                                                <span className="font-medium text-green-600">{userDetailData.auctions?.won || 0}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sales Stats */}
                                    <div className="p-3 bg-green-50/50 rounded-lg border border-green-100">
                                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                                            <ShoppingBag className="w-4 h-4 text-green-600" />
                                            Statistici Tranzacții
                                        </h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Cumpărat:</span>
                                                <span className="font-medium">{userDetailData.orders?.as_buyer?.length || 0} comenzi ({userDetailData.totals?.total_bought || 0} RON)</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Vândut:</span>
                                                <span className="font-medium">{userDetailData.orders?.as_seller?.length || 0} comenzi ({userDetailData.totals?.total_sold || 0} RON)</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Fraud Assessment */}
                                    {userDetailData.fraud_assessment && (
                                        <div className={`p-3 rounded-lg border ${
                                            userDetailData.fraud_assessment.risk_level === 'high' ? 'bg-red-50 border-red-200' :
                                            userDetailData.fraud_assessment.risk_level === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                                            'bg-green-50 border-green-200'
                                        }`}>
                                            <div className="flex items-center gap-2 mb-2">
                                                <AlertTriangle className={`w-4 h-4 ${
                                                    userDetailData.fraud_assessment.risk_level === 'high' ? 'text-red-600' :
                                                    userDetailData.fraud_assessment.risk_level === 'medium' ? 'text-yellow-600' :
                                                    'text-green-600'
                                                }`} />
                                                <span className="font-medium">Risc: {userDetailData.fraud_assessment.risk_level}</span>
                                                <Badge variant="outline">{userDetailData.fraud_assessment.score}/100</Badge>
                                            </div>
                                            {userDetailData.fraud_assessment.indicators?.length > 0 && (
                                                <ul className="text-sm text-muted-foreground list-disc list-inside">
                                                    {userDetailData.fraud_assessment.indicators.map((ind, i) => (
                                                        <li key={i}>{ind}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    )}

                                    {/* Additional Info */}
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Înregistrat:</span>
                                            <span>{formatDate(selectedUser.created_at)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Rol:</span>
                                            <span>{getRoleBadge(selectedUser.role)}</span>
                                        </div>
                                        
                                        {/* Warnings Section */}
                                        <div className="flex justify-between items-center py-2 border-t border-b">
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className={`w-4 h-4 ${(selectedUser.warnings_count || 0) > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                                                <span className={`font-medium ${(selectedUser.warnings_count || 0) >= 5 ? 'text-red-600' : ''}`}>
                                                    Avertismente: {selectedUser.warnings_count || 0}/5
                                                </span>
                                            </div>
                                            <div className="flex gap-1">
                                                <Button
                                                    size="icon"
                                                    variant="outline"
                                                    className="h-7 w-7 text-red-600 hover:bg-red-50"
                                                    onClick={() => handleRemoveWarning(selectedUser)}
                                                    disabled={(selectedUser.warnings_count || 0) <= 0}
                                                >
                                                    <Minus className="w-3 h-3" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="outline"
                                                    className="h-7 w-7 text-orange-600 hover:bg-orange-50"
                                                    onClick={() => handleAddWarning(selectedUser)}
                                                    disabled={(selectedUser.warnings_count || 0) >= 5}
                                                >
                                                    <Plus className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </div>
                                        
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Dispute:</span>
                                            <span>{userDetailData.orders?.disputed || 0}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Rapoarte primite:</span>
                                            <span>{userDetailData.reports?.about_user?.length || 0}</span>
                                        </div>
                                    </div>

                                    <Separator />
                                    
                                    {/* Admin Notes Section */}
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                                <FileText className="w-4 h-4" />
                                                Observații Admin
                                            </p>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={handleSaveAdminNotes}
                                                disabled={adminNotesSaving}
                                                className="h-7 text-xs"
                                            >
                                                {adminNotesSaving ? (
                                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                ) : (
                                                    <Save className="w-3 h-3 mr-1" />
                                                )}
                                                Salvează
                                            </Button>
                                        </div>
                                        <Textarea
                                            value={adminNotes}
                                            onChange={(e) => setAdminNotes(e.target.value)}
                                            placeholder="Notează observații despre acest utilizator... (vizibile doar pentru administratori)"
                                            rows={3}
                                            className="text-sm resize-none"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Aceste note sunt private și vizibile doar pentru administratori.
                                        </p>
                                    </div>

                                    <Separator />

                                    {/* Action Buttons */}
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium text-muted-foreground">Acțiuni</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => navigate(`/user/${selectedUser.username}`)}
                                                className="justify-start text-primary hover:text-primary/80"
                                            >
                                                <Package className="w-4 h-4 mr-2" />
                                                Vânzări
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => handleViewUserLog(selectedUser)}
                                                className="justify-start text-purple-600 hover:text-purple-700"
                                            >
                                                <Activity className="w-4 h-4 mr-2" />
                                                Jurnal
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => {
                                                    setMessageDialog({ open: true, user: selectedUser });
                                                    setMessageContent('');
                                                }}
                                                className="justify-start text-blue-600 hover:text-blue-700"
                                            >
                                                <MessageCircle className="w-4 h-4 mr-2" />
                                                Trimite mesaj
                                            </Button>
                                            {currentUserRole === 'admin' && (
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => navigate(`/admin?tab=conversations&user=${selectedUser.user_id}`)}
                                                    className="justify-start text-teal-600 hover:text-teal-700"
                                                >
                                                    <MessagesSquare className="w-4 h-4 mr-2" />
                                                    Conversații
                                                </Button>
                                            )}
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => handleViewUserNotes(selectedUser)}
                                                className="justify-start text-amber-600 hover:text-amber-700"
                                            >
                                                <FileText className="w-4 h-4 mr-2" />
                                                Vezi notițe
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => handleOpenAddNote(selectedUser)}
                                                className="justify-start text-emerald-600 hover:text-emerald-700"
                                            >
                                                <Plus className="w-4 h-4 mr-2" />
                                                Adaugă notiță
                                            </Button>
                                            {currentUserRole === 'admin' && (
                                                <>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => handleOpenEditNameDialog(selectedUser)}
                                                        className="justify-start text-indigo-600 hover:text-indigo-700"
                                                    >
                                                        <User className="w-4 h-4 mr-2" />
                                                        Editează nume
                                                    </Button>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => handleOpenLoginLog(selectedUser)}
                                                        className="justify-start text-cyan-600 hover:text-cyan-700"
                                                    >
                                                        <History className="w-4 h-4 mr-2" />
                                                        Istoric login
                                                    </Button>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => handleOpenPasswordDialog(selectedUser)}
                                                        className="justify-start"
                                                    >
                                                        <KeyRound className="w-4 h-4 mr-2" />
                                                        Schimbă parola
                                                    </Button>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => setRoleDialog({ open: true, user: selectedUser, newRole: selectedUser.role || 'user' })}
                                                        className="justify-start"
                                                    >
                                                        <Shield className="w-4 h-4 mr-2" />
                                                        Schimbă rol
                                                    </Button>
                                                </>
                                            )}
                                            {selectedUser.is_banned ? (
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => onUnbanUser(selectedUser.user_id)}
                                                    className="justify-start text-green-600 hover:text-green-700"
                                                >
                                                    <CheckCircle className="w-4 h-4 mr-2" />
                                                    Deblochează
                                                </Button>
                                            ) : (
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => setBanDialog({ open: true, user: selectedUser, reason: '' })}
                                                    className="justify-start text-orange-600 hover:text-orange-700"
                                                >
                                                    <Ban className="w-4 h-4 mr-2" />
                                                    Blochează
                                                </Button>
                                            )}
                                            {currentUserRole === 'admin' && (
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => setDeleteDialog({ open: true, user: selectedUser })}
                                                    className="justify-start text-red-600 hover:text-red-700"
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Șterge cont
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </CardContent>
                    </Card>
                    </div>
                )}
            </div>

            {/* Password Change Dialog */}
            <Dialog open={passwordDialog.open} onOpenChange={(open) => !open && handleClosePasswordDialog()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Schimbă Parola pentru {passwordDialog.user?.username}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-password">Parola Nouă</Label>
                            <Input
                                id="new-password"
                                type="password"
                                placeholder="Minim 8 caractere"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                data-testid="admin-new-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirm-password">Confirmă Parola</Label>
                            <Input
                                id="confirm-password"
                                type="password"
                                placeholder="Repetă parola"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                data-testid="admin-confirm-password-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={handleClosePasswordDialog} disabled={isSubmitting}>
                            Anulează
                        </Button>
                        <Button onClick={handleChangePassword} disabled={isSubmitting} data-testid="admin-change-password-btn">
                            {isSubmitting ? 'Se procesează...' : 'Schimbă Parola'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Role Change Dialog */}
            <Dialog open={roleDialog.open} onOpenChange={(o) => setRoleDialog({ ...roleDialog, open: o })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Schimbă rolul utilizatorului</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p>Utilizator: <strong>{roleDialog.user?.username}</strong></p>
                        <Select value={roleDialog.newRole} onValueChange={(v) => setRoleDialog({ ...roleDialog, newRole: v })}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="user">Utilizator</SelectItem>
                                <SelectItem value="moderator">Moderator</SelectItem>
                                <SelectItem value="admin">Administrator</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRoleDialog({ open: false, user: null, newRole: 'user' })} disabled={isSubmitting}>
                            Anulează
                        </Button>
                        <Button onClick={handleChangeRole} disabled={isSubmitting}>
                            {isSubmitting ? 'Se procesează...' : 'Salvează'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Ban Dialog */}
            <Dialog open={banDialog.open} onOpenChange={(o) => setBanDialog({ ...banDialog, open: o })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Blochează utilizator</DialogTitle>
                        <DialogDescription>
                            Sigur vrei să blochezi utilizatorul <strong>{banDialog.user?.username}</strong>?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="ban-reason">Motiv (opțional)</Label>
                            <Input
                                id="ban-reason"
                                placeholder="Introduceți motivul blocării..."
                                value={banDialog.reason}
                                onChange={(e) => setBanDialog({ ...banDialog, reason: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBanDialog({ open: false, user: null, reason: '' })} disabled={isSubmitting}>
                            Anulează
                        </Button>
                        <Button variant="destructive" onClick={handleBanUser} disabled={isSubmitting}>
                            {isSubmitting ? 'Se procesează...' : 'Blochează'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.open} onOpenChange={(o) => setDeleteDialog({ ...deleteDialog, open: o })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Șterge cont utilizator</DialogTitle>
                        <DialogDescription>
                            Această acțiune este ireversibilă. Sigur vrei să ștergi contul utilizatorului <strong>{deleteDialog.user?.username}</strong>?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null })} disabled={isSubmitting}>
                            Anulează
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteUser} disabled={isSubmitting}>
                            {isSubmitting ? 'Se procesează...' : 'Șterge definitiv'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Message Dialog */}
            <Dialog open={messageDialog.open} onOpenChange={(o) => {
                setMessageDialog({ ...messageDialog, open: o });
                if (!o) setMessageContent('');
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Trimite mesaj către {messageDialog.user?.username}</DialogTitle>
                        <DialogDescription>
                            Mesajul va fi trimis direct utilizatorului și va crea o conversație nouă.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="message-content">Mesaj</Label>
                            <Textarea
                                id="message-content"
                                placeholder="Scrie mesajul tău..."
                                value={messageContent}
                                onChange={(e) => setMessageContent(e.target.value)}
                                rows={4}
                                className="resize-none"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setMessageDialog({ open: false, user: null });
                            setMessageContent('');
                        }} disabled={isSubmitting}>
                            Anulează
                        </Button>
                        <Button onClick={handleSendMessage} disabled={isSubmitting || !messageContent.trim()}>
                            {isSubmitting ? 'Se trimite...' : 'Trimite mesaj'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View User Notes Dialog */}
            <Dialog open={userNotesDialog.open} onOpenChange={(open) => !open && setUserNotesDialog({ open: false, user: null, notes: [], loading: false })}>
                <DialogContent className="max-w-lg max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-amber-600" />
                            Notițe pentru {userNotesDialog.user?.name || userNotesDialog.user?.username}
                        </DialogTitle>
                        <DialogDescription>
                            Toate notițele administrative despre acest utilizator
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        {userNotesDialog.loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : userNotesDialog.notes.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>Nu există notițe pentru acest utilizator</p>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                                {userNotesDialog.notes.map((note, idx) => (
                                    <div key={note.report_id || idx} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                                        <p className="text-sm text-gray-800">{note.note}</p>
                                        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                                            <span>de <strong>{note.author}</strong></span>
                                            <span>{formatDate(note.date)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => handleOpenAddNote(userNotesDialog.user)}
                            className="mr-auto"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Adaugă notiță
                        </Button>
                        <Button onClick={() => setUserNotesDialog({ open: false, user: null, notes: [], loading: false })}>
                            Închide
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Note Dialog */}
            <Dialog open={addNoteDialog.open} onOpenChange={(open) => !open && setAddNoteDialog({ open: false, user: null, note: '' })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="w-5 h-5 text-emerald-600" />
                            Adaugă notiță pentru {addNoteDialog.user?.name || addNoteDialog.user?.username}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {addNoteDialog.user && (
                            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                                <Avatar className="w-10 h-10">
                                    <AvatarImage src={addNoteDialog.user.picture} />
                                    <AvatarFallback>{getInitials(addNoteDialog.user.name || addNoteDialog.user.username)}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <p className="font-medium">{addNoteDialog.user.name || addNoteDialog.user.username}</p>
                                    <p className="text-xs text-muted-foreground">{addNoteDialog.user.email}</p>
                                </div>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="add-note-content">Conținutul notiței</Label>
                            <Textarea
                                id="add-note-content"
                                placeholder="Scrie notița despre acest utilizator..."
                                value={addNoteDialog.note}
                                onChange={(e) => setAddNoteDialog(prev => ({ ...prev, note: e.target.value }))}
                                rows={4}
                                className="resize-none"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddNoteDialog({ open: false, user: null, note: '' })} disabled={addingNote}>
                            Anulează
                        </Button>
                        <Button onClick={handleAddUserNote} disabled={addingNote || !addNoteDialog.note.trim()}>
                            {addingNote ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Se adaugă...
                                </>
                            ) : (
                                'Adaugă notiță'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Name Dialog */}
            <Dialog open={editNameDialog.open} onOpenChange={(open) => !open && setEditNameDialog({ open: false, user: null, newUsername: '', newName: '' })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit className="w-5 h-5 text-indigo-600" />
                            Editează numele utilizatorului
                        </DialogTitle>
                        <DialogDescription>
                            Modifică username-ul sau numele complet al utilizatorului.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {editNameDialog.user && (
                            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                                <Avatar className="w-10 h-10">
                                    <AvatarImage src={editNameDialog.user.picture} />
                                    <AvatarFallback>{getInitials(editNameDialog.user.name || editNameDialog.user.username)}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <p className="font-medium">{editNameDialog.user.name || editNameDialog.user.username}</p>
                                    <p className="text-xs text-muted-foreground">{editNameDialog.user.email}</p>
                                </div>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="edit-username">Username</Label>
                            <Input
                                id="edit-username"
                                placeholder="Username (minim 3 caractere)"
                                value={editNameDialog.newUsername}
                                onChange={(e) => setEditNameDialog(prev => ({ ...prev, newUsername: e.target.value }))}
                            />
                            <p className="text-xs text-muted-foreground">Username-ul este folosit pentru login și URL-ul profilului</p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-name">Nume complet (opțional)</Label>
                            <Input
                                id="edit-name"
                                placeholder="Nume și prenume"
                                value={editNameDialog.newName}
                                onChange={(e) => setEditNameDialog(prev => ({ ...prev, newName: e.target.value }))}
                            />
                            <p className="text-xs text-muted-foreground">Numele afișat public pe platformă</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditNameDialog({ open: false, user: null, newUsername: '', newName: '' })} disabled={isSubmitting}>
                            Anulează
                        </Button>
                        <Button onClick={handleSaveUserName} disabled={isSubmitting || !editNameDialog.newUsername.trim()}>
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Se salvează...
                                </>
                            ) : (
                                'Salvează'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Login Log Dialog */}
            <Dialog open={loginLogDialog.open} onOpenChange={(open) => !open && setLoginLogDialog({ open: false, user: null, logs: [], loading: false })}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="w-5 h-5" />
                            Istoric autentificări - {loginLogDialog.user?.name || loginLogDialog.user?.username}
                        </DialogTitle>
                        <DialogDescription>
                            Ultimele 100 de autentificări
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto">
                        {loginLogDialog.loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : loginLogDialog.logs.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <LogIn className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>Nicio autentificare găsită</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {loginLogDialog.logs.map((log, idx) => {
                                    const ua = parseUserAgentShort(log.user_agent);
                                    return (
                                        <div 
                                            key={idx}
                                            className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3 flex-1">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                                    {ua.device === 'Mobile' ? (
                                                        <Smartphone className="w-4 h-4 text-primary" />
                                                    ) : (
                                                        <Monitor className="w-4 h-4 text-primary" />
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <Badge variant="outline" className="text-xs">
                                                            {log.login_method === 'google_login' ? 'Google' : 
                                                             log.login_method === 'facebook_login' ? 'Facebook' : 
                                                             log.login_method === 'login_2fa' ? '2FA' : 'Email'}
                                                        </Badge>
                                                        <span className="text-xs text-muted-foreground">
                                                            {ua.browser} • {ua.device}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground truncate mt-1" title={log.user_agent}>
                                                        {log.user_agent?.substring(0, 60)}...
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex flex-col sm:items-end gap-1 text-xs shrink-0">
                                                <div className="flex items-center gap-1">
                                                    <Globe className="w-3 h-3" />
                                                    <span className="font-mono">{log.ip_address}</span>
                                                </div>
                                                <span className="text-muted-foreground">
                                                    {new Date(log.timestamp).toLocaleString('ro-RO', { 
                                                        day: '2-digit', 
                                                        month: '2-digit', 
                                                        year: 'numeric',
                                                        hour: '2-digit', 
                                                        minute: '2-digit' 
                                                    })}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminUsersTab;
